import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import Footer from '../component/common/Footer';
import LibraryNavbar from '../component/core/LibrarySection/LibraryNavbar';
import Breadcrumb from '../component/common/Breadcrumb';
import MusicCard from '../component/core/MusicSection/MusicCard';
import LyricsModal from '../component/core/MusicSection/LyricsModal';
import { getMusicByType, getMusicById } from '../Services/musicService';
import { useLanguage } from '../i18n/LanguageContext';

const SongsByType = () => {
  const { sectionName } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [musicData, setMusicData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [showLyricsModal, setShowLyricsModal] = useState(false);
  const [lyrics, setLyrics] = useState(null);

  // Type configuration
  const typeConfig = {
    aarti: {
      title: "Aarti",
      translatedTitle: t('arti'),
      description: t('artiDescription'),
      icon: "🙏",
      color: "from-orange-500 to-red-500"
    },
    chalisa: {
      title: "Chalisa",
      translatedTitle: t('chalisa'),
      description: t('chalisaDescription'),
      icon: "📖",
      color: "from-blue-500 to-purple-500"
    },
    mantra: {
      title: "Mantra",
      translatedTitle: t('mantra'),
      description: t('mantraDescription'),
      icon: "🕉️",
      color: "from-green-500 to-teal-500"
    },
    meditation: {
      title: "Meditation",
      translatedTitle: t('meditation'),
      description: t('meditationDescription'),
      icon: "🧘",
      color: "from-purple-500 to-pink-500"
    }
  };

  const currentType = typeConfig[sectionName?.toLowerCase()];

  const fetchMusicByType = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getMusicByType(sectionName);
      if (response.status === "success") {
        setMusicData(response.data);
      } else {
        setError("Failed to fetch music data");
      }
    } catch (err) {
      setError("Error loading music data");
      console.error("Error fetching music:", err);
    } finally {
      setLoading(false);
    }
  }, [sectionName]);

  useEffect(() => {
    if (sectionName) {
      fetchMusicByType();
    }
  }, [sectionName, fetchMusicByType]);

  // Audio handling is now managed by AudioContext

  const handleShowLyrics = async (music) => {
    try {
      setSelectedMusic(music);
      setShowLyricsModal(true);
      const response = await getMusicById(music.id);
      if (response.status === "success") {
        setLyrics(response.data);
      }
    } catch (err) {
      console.error("Error fetching lyrics:", err);
    }
  };

  const handleCloseLyrics = () => {
    setShowLyricsModal(false);
    setSelectedMusic(null);
    setLyrics(null);
  };

  const handleBack = () => {
    navigate('/songs');
  };

  if (!currentType) {
    return (
      <>
        <LibraryNavbar sectionTitle="" subsectionName="" />
        <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">{t('pageNotFound')}</h1>
            <p className="text-gray-600 mb-6">{t('requestedCategoryNotExist')}</p>
            <button 
              onClick={handleBack}
              className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
              {t('backToSongs')}
            </button>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <LibraryNavbar sectionTitle="" subsectionName="" />
        <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('loadingMusicType').replace('{type}', currentType.translatedTitle)}</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <LibraryNavbar sectionTitle="" subsectionName="" />
        <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pt-20 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={fetchMusicByType}
              className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors mr-4"
            >
{t('tryAgain')}
            </button>
            <button 
              onClick={handleBack}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              {t('backToSongs')}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LibraryNavbar sectionTitle="" subsectionName="" />
      
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pt-20">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <Breadcrumb 
            customItems={[
              { label: 'Home', path: '/', icon: <span>🏠</span> },
              { label: 'Songs', path: '/songs' },
              { label: currentType.title, path: `/songs/${sectionName}`, isLast: true }
            ]}
          />

          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <button
                onClick={handleBack}
                className="mr-4 p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-all duration-300"
              >
                <FaArrowLeft className="text-gray-600" />
              </button>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-orange-800 mb-2">
                  {currentType.icon} {currentType.translatedTitle}
                </h1>
                <p className="text-xl text-gray-700">
                  {currentType.description}
                </p>
              </div>
            </div>
          </div>

          {/* Music List */}
          {musicData.length > 0 ? (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  {t('songsAvailableCount').replace('{count}', musicData.length)}
                </h2>
                <div className="space-y-3">
                  {musicData.map((music) => (
                    <MusicCard
                      key={music.id}
                      music={music}
                      onShowLyrics={handleShowLyrics}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="bg-white rounded-2xl shadow-lg p-12 max-w-2xl mx-auto">
                <div className="text-6xl mb-6">{currentType.icon}</div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  {t('noMusicTypeYet').replace('{type}', currentType.translatedTitle)}
                </h2>
                <p className="text-gray-600 mb-8">
                  {t('workingOnAdding').replace('{type}', currentType.translatedTitle.toLowerCase())}
                </p>
                <button 
                  onClick={handleBack}
                  className="bg-orange-500 text-white px-8 py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors"
                >
                  {t('backToAllSongs')}
                </button>
              </div>
            </div>
          )}

          {/* Additional Info */}
          {musicData.length > 0 && (
            <div className="mt-16 text-center">
              <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  🌟 {t('moreTypeComingSoon').replace('{type}', currentType.translatedTitle)}
                </h3>
                <p className="text-gray-600">
                  {t('continuouslyAddingType').replace('{type}', currentType.translatedTitle.toLowerCase())}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lyrics Modal */}
      <LyricsModal
        isOpen={showLyricsModal}
        onClose={handleCloseLyrics}
        music={selectedMusic}
        lyrics={lyrics}
      />

      <Footer />
    </>
  );
};

export default SongsByType;
